import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  NumberInput,
  Paper,
  Pagination,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, CheckCircle, ClipboardList, Star, User, XCircle } from 'lucide-react'
import { RichText } from '../../components/common/RichText'
import { stripLeadingQuestionNumber } from '../../utils/questionText'
import {
  assignmentsApi,
  Assignment,
  AssignmentChoice,
  AssignmentStudent,
  SubmissionDetailResponse,
} from '../../api/assignments'

const STATUS_COLOR: Record<string, string> = {
  assigned: 'blue',
  processing: 'grape',
  submitted: 'orange',
  graded: 'green',
  reviewed: 'green',
}

const QUEUE_PAGE_SIZE = 5

const TYPE_FILTER_OPTIONS = [
  { value: 'quiz', label: 'Quiz' },
  { value: 'enhance', label: 'Enhance' },
  { value: 'test', label: 'Test' },
  { value: 'mock', label: 'Mock' },
]

/** Match choice ids from API (avoids number vs string mismatches). */
function sameChoiceId(a: number | null | undefined, b: number | null | undefined) {
  if (a == null || b == null) return false
  return Number(a) === Number(b)
}

function sameChoiceLabel(
  apiLabel: string | null | undefined,
  choiceLabel: string
) {
  if (!apiLabel || !choiceLabel) return false
  return apiLabel.trim().toUpperCase() === choiceLabel.trim().toUpperCase()
}

/** Resolve choice by id (preferred) or by letter label from API submission payload. */
function findChoiceForSubmission(
  choices: AssignmentChoice[],
  choiceId: number | null | undefined,
  choiceLabel: string | null | undefined
) {
  if (choiceId != null) {
    const byId = choices.find((c) => sameChoiceId(c.id, choiceId))
    if (byId) return byId
  }
  if (choiceLabel) {
    return choices.find((c) => sameChoiceLabel(choiceLabel, c.choice_label))
  }
  return undefined
}

function assignmentTypeValue(assignment: Assignment) {
  return (assignment.assignment_type || assignment.action || '').trim().toLowerCase()
}

function assignmentMatchesTypeFilters(assignment: Assignment, selectedTypes: string[]) {
  if (selectedTypes.length === 0) return true

  const type = assignmentTypeValue(assignment)
  if (selectedTypes.includes(type)) return true

  // Older evaluation assignments may still be labeled as "evaluate".
  return selectedTypes.includes('mock') && type === 'evaluate'
}

function assignmentTypeLabel(assignment: Assignment) {
  const type = assignmentTypeValue(assignment)
  if (type === 'evaluate') return 'Mock'
  return TYPE_FILTER_OPTIONS.find((option) => option.value === type)?.label || type || 'Assignment'
}

function submittedTimestamp(student: AssignmentStudent) {
  if (!student.submitted_at) return 0
  const time = new Date(student.submitted_at).getTime()
  return Number.isNaN(time) ? 0 : time
}

interface ReviewQueueItem {
  assignment: Assignment
  student: AssignmentStudent
}

export function TutorGrading() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<ReviewQueueItem[]>([])

  // Currently selected for grading
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(
    searchParams.get('assignment') ? Number(searchParams.get('assignment')) : null
  )
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    searchParams.get('student') ? Number(searchParams.get('student')) : null
  )
  const [selectedStudent, setSelectedStudent] = useState<AssignmentStudent | null>(null)
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null)
  const [selectedDetail, setSelectedDetail] = useState<SubmissionDetailResponse | null>(null)

  // Grading form
  const [score, setScore] = useState<number | string>('')
  const [feedback, setFeedback] = useState('')
  const [grading, setGrading] = useState(false)
  const [studentFilterId, setStudentFilterId] = useState<string | null>(null)
  const [onlyUngraded, setOnlyUngraded] = useState(false)
  const [assignmentTypeFilters, setAssignmentTypeFilters] = useState<string[]>([])
  const [activePage, setActivePage] = useState(1)

  const studentFilterOptions = useMemo(() => {
    const byId = new Map<number, string>()
    for (const { student } of items) {
      if (!byId.has(student.student_id)) {
        byId.set(student.student_id, student.student_name || `Student #${student.student_id}`)
      }
    }
    return Array.from(byId.entries())
      .map(([id, label]) => ({ value: String(id), label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  }, [items])

  const filteredItems = useMemo(() => {
    if (!studentFilterId) return items
    const id = Number(studentFilterId)
    return items.filter(({ student }) => student.student_id === id)
  }, [items, studentFilterId])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / QUEUE_PAGE_SIZE))
  const pagedItems = filteredItems.slice(
    (activePage - 1) * QUEUE_PAGE_SIZE,
    activePage * QUEUE_PAGE_SIZE
  )

  useEffect(() => {
    if (!studentFilterId) return
    const id = Number(studentFilterId)
    if (selectedStudent && selectedStudent.student_id !== id) {
      setSelectedStudent(null)
      setSelectedAssignment(null)
      setSelectedDetail(null)
    }
  }, [studentFilterId, selectedStudent])

  useEffect(() => {
    setActivePage(1)
  }, [studentFilterId, onlyUngraded, assignmentTypeFilters])

  useEffect(() => {
    if (activePage > totalPages) {
      setActivePage(totalPages)
    }
  }, [activePage, totalPages])

  useEffect(() => {
    if (!selectedAssignment || !selectedStudent) return
    const selectedStillVisible = filteredItems.some(
      ({ assignment, student }) =>
        assignment.id === selectedAssignment.id && student.id === selectedStudent.id
    )
    if (!selectedStillVisible) {
      setSelectedStudent(null)
      setSelectedAssignment(null)
      setSelectedDetail(null)
    }
  }, [filteredItems, selectedAssignment, selectedStudent])

  const load = async (selectFromParams = true) => {
    setLoading(true)
    try {
      const res = await assignmentsApi.list()
      const assignments = res.data as Assignment[]
      const candidates = assignments.filter((assignment) => {
        if (!assignmentMatchesTypeFilters(assignment, assignmentTypeFilters)) return false
        return true
      })
      const queueItems: ReviewQueueItem[] = []
      await Promise.all(
        candidates.map(async (a) => {
          const detail = await assignmentsApi.get(a.id)
          const reviewable = detail.data.students.filter((s) => {
            if (onlyUngraded) return s.status !== 'reviewed'
            return true
          })
          for (const student of reviewable) {
            queueItems.push({ assignment: a, student })
          }
        })
      )
      queueItems.sort((a, b) => {
        const byDate = submittedTimestamp(b.student) - submittedTimestamp(a.student)
        if (byDate !== 0) return byDate
        return b.assignment.id - a.assignment.id
      })
      setItems(queueItems)

      // If URL params set, find the student
      if (selectFromParams && selectedAssignmentId && selectedStudentId) {
        for (const item of queueItems) {
          if (item.assignment.id === selectedAssignmentId) {
            if (item.student.student_id === selectedStudentId) {
              await selectForGrading(item.assignment, item.student)
              break
            }
          }
        }
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load submissions', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [onlyUngraded, assignmentTypeFilters])

  const selectForGrading = async (assignment: Assignment, student: AssignmentStudent) => {
    setSelectedAssignment(assignment)
    setSelectedStudent(student)
    // URL/API expect the student's user id (tbl_user.id), not AssignmentStudent.id
    setSelectedStudentId(student.student_id)
    setSelectedAssignmentId(assignment.id)
    try {
      const detail = await assignmentsApi.getSubmission(assignment.id, student.student_id)
      setSelectedDetail(detail.data)
      setScore(student.score !== null ? Math.round(student.score) : Math.round(detail.data.auto_score))
      setFeedback(student.tutor_feedback || '')
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load submission details.', color: 'red' })
    }
  }

  const handleGrade = async () => {
    if (!selectedAssignment || !selectedStudent) return
    setGrading(true)
    try {
      await assignmentsApi.grade(selectedAssignment.id, {
        student_id: selectedStudent.student_id,
        score: score !== '' ? Number(score) : undefined,
        tutor_feedback: feedback.trim() || undefined,
        answer_overrides: [],
      })
      notifications.show({ title: 'Graded!', message: `Feedback sent to ${selectedStudent.student_name || 'student'}.`, color: 'green' })
      setSelectedStudent(null)
      setSelectedAssignment(null)
      setSelectedDetail(null)
      setSelectedStudentId(null)
      setSelectedAssignmentId(null)
      await load(false)
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed to grade', color: 'red' })
    } finally {
      setGrading(false)
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  const totalPending = filteredItems.filter(({ student }) => student.status === 'submitted').length

  return (
    <Box style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left: Submissions list */}
      <Paper radius={0} withBorder style={{ width: 340, borderTop: 0, borderLeft: 0, borderBottom: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <Box style={{ padding: '20px 16px 12px', background: 'var(--sat-bg)', borderBottom: '1px solid var(--sat-border-warm)' }}>
          <Group gap={8} justify="space-between" align="center">
            <Group gap={8}>
              <ClipboardList size={18} color="var(--sat-red)" />
              <Text fw={700} c="var(--sat-text)">Review Queue</Text>
              {totalPending > 0 && (
                <Badge size="sm" color="orange">{totalPending}</Badge>
              )}
            </Group>
            <Checkbox
              size="xs"
              label="Only Un-Reviewed"
              checked={onlyUngraded}
              onChange={(event) => setOnlyUngraded(event.currentTarget.checked)}
            />
          </Group>
        </Box>

        <Box style={{ padding: '0 16px 12px', background: 'var(--sat-bg)', borderBottom: '1px solid var(--sat-border-warm)' }}>
          <Select
            label="Student"
            placeholder="All students"
            data={studentFilterOptions}
            value={studentFilterId}
            onChange={setStudentFilterId}
            searchable
            clearable
            leftSection={<User size={16} />}
            comboboxProps={{ withinPortal: true }}
          />
          <Box mt={12}>
            <Text size="xs" fw={500} c="var(--sat-text)" mb={6}>
              Assignment type
            </Text>
            <Checkbox.Group value={assignmentTypeFilters} onChange={setAssignmentTypeFilters}>
              <Group gap="xs">
                {TYPE_FILTER_OPTIONS.map((option) => (
                  <Checkbox key={option.value} size="xs" value={option.value} label={option.label} />
                ))}
              </Group>
            </Checkbox.Group>
          </Box>
        </Box>

        <Box style={{ overflow: 'auto', flex: 1 }}>
          {items.length === 0 ? (
            <Box style={{ padding: 24, textAlign: 'center' }}>
              <CheckCircle size={40} color="var(--sat-green)" style={{ margin: '0 auto 12px' }} />
              <Text size="sm" c="var(--sat-text-soft)">No submissions match the current queue filters.</Text>
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box style={{ padding: 24, textAlign: 'center' }}>
              <Text size="sm" c="var(--sat-text-soft)">No submissions match this student filter.</Text>
            </Box>
          ) : (
            <Stack gap={0}>
              {pagedItems.map(({ assignment, student }) => {
                const isSelected = selectedStudent?.id === student.id
                return (
                  <Box
                    key={`${assignment.id}-${student.id}`}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      background: isSelected ? 'var(--sat-red-soft)' : 'var(--sat-surface)',
                      borderLeft: isSelected ? '3px solid var(--sat-red)' : '3px solid transparent',
                      borderBottom: '1px solid var(--sat-border-faint)',
                    }}
                    onClick={() => { void selectForGrading(assignment, student) }}
                  >
                    <Group justify="space-between" align="flex-start" gap={8}>
                      <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={isSelected ? 600 : 400} c="var(--sat-text)" lineClamp={1}>
                          {student.student_name || `Student #${student.student_id}`}
                        </Text>
                        <Text size="xs" fw={700} c="var(--sat-text-subtle)" lineClamp={1}>
                          {assignment.title}
                        </Text>
                      </Box>
                      <Badge size="xs" color={STATUS_COLOR[student.status] || 'gray'} variant="light">
                        {student.status}
                      </Badge>
                    </Group>
                    <Group gap={6} mt={4}>
                      <Badge size="xs" variant="light" color="gray">
                        {assignmentTypeLabel(assignment)}
                      </Badge>
                      <Text size="xs" c="var(--sat-text-faint)">
                        Submitted {student.submitted_at ? new Date(student.submitted_at).toLocaleDateString() : '—'}
                      </Text>
                    </Group>
                    <Text size="xs" c="var(--sat-text-faint)" mt={2}>
                      Score {student.score !== null ? `${Math.round(student.score)}%` : 'Auto pending'}
                    </Text>
                  </Box>
                )
              })}
            </Stack>
          )}
        </Box>
        {filteredItems.length > QUEUE_PAGE_SIZE && (
          <Box style={{ padding: '10px 16px', borderTop: '1px solid var(--sat-border-warm)', background: 'var(--sat-bg)' }}>
            <Pagination
              size="sm"
              total={totalPages}
              value={activePage}
              onChange={setActivePage}
              withEdges
            />
          </Box>
        )}
      </Paper>

      {/* Right: Grading panel */}
      <Box style={{ flex: 1, overflow: 'auto', padding: 32 }}>
        {!selectedStudent || !selectedDetail ? (
          <Box style={{ textAlign: 'center', paddingTop: 80 }}>
            <Star size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
            <Text c="var(--sat-text-faint)">Select a submission from the left to review it.</Text>
          </Box>
        ) : (
          <Stack gap={20} style={{ maxWidth: 860 }}>
            <Group>
              <ActionIcon variant="subtle" color="gray" onClick={() => { setSelectedStudent(null); setSelectedAssignment(null); setSelectedDetail(null) }}>
                <ArrowLeft size={18} />
              </ActionIcon>
              <Box>
                <Title order={3} c="var(--sat-text)">{selectedStudent.student_name || `Student #${selectedStudent.student_id}`}</Title>
                <Text size="sm" c="var(--sat-text-soft)">{selectedAssignment?.title}</Text>
              </Box>
            </Group>

            <Paper shadow="xs" radius="md" p="md" style={{ background: 'var(--sat-bg)' }}>
              <Group gap={32} wrap="wrap">
                <Box>
                  <Text size="xs" c="var(--sat-text-faint)">Status</Text>
                  <Badge color={STATUS_COLOR[selectedStudent.status] || 'gray'}>{selectedStudent.status}</Badge>
                </Box>
                <Box>
                  <Text size="xs" c="var(--sat-text-faint)">Submitted</Text>
                  <Text size="sm" fw={600} c="var(--sat-text)">
                    {selectedStudent.submitted_at ? new Date(selectedStudent.submitted_at).toLocaleString() : '—'}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="var(--sat-text-faint)">Auto Score</Text>
                  <Text size="sm" fw={600} c="var(--sat-green)">{Math.round(selectedDetail.auto_score)}%</Text>
                </Box>
                {selectedStudent.graded_at && (
                  <Box>
                    <Text size="xs" c="var(--sat-text-faint)">Graded</Text>
                    <Text size="sm" fw={600} c="var(--sat-text)">
                      {new Date(selectedStudent.graded_at).toLocaleString()}
                    </Text>
                  </Box>
                )}
              </Group>
            </Paper>

            <Divider label="Student Results" labelPosition="left" />

            {selectedDetail.questions.map((question, index) => (
              <Paper key={question.id} shadow="sm" radius="md" p="xl">
                <Stack gap={14}>
                  <Group justify="space-between" align="flex-start">
                    <Text fw={700} c="var(--sat-text)">Question {index + 1}</Text>
                    <Badge color={question.is_correct ? 'green' : 'red'} variant="light">
                      {question.is_correct ? 'Correct' : 'Incorrect'}
                    </Badge>
                  </Group>

                  {stripLeadingQuestionNumber(question.passage_text) && (
                    <Paper p="md" radius="md" style={{ background: 'var(--sat-bg)' }}>
                      <RichText size="sm">{stripLeadingQuestionNumber(question.passage_text)}</RichText>
                    </Paper>
                  )}

                  <RichText size="md">{stripLeadingQuestionNumber(question.prompt)}</RichText>

                  {question.question_type === 'spr' ? (
                    <Stack gap={8}>
                      <Paper
                        p={14}
                        radius="md"
                        style={{
                          background: question.is_correct ? 'var(--sat-green-soft-subtle)' : 'var(--sat-red-soft)',
                          border: `2px solid ${question.is_correct ? 'var(--sat-green)' : 'var(--sat-red)'}`,
                        }}
                      >
                        <Group justify="space-between" align="center" wrap="nowrap">
                          <div style={{ minWidth: 0 }}>
                            <Text size="xs" fw={700} c="var(--sat-text-faint)" mb={2}>Student&apos;s answer</Text>
                            <Text fw={700} size="lg" style={{ wordBreak: 'break-word' }}>
                              {question.text_answer?.trim() ? question.text_answer : '(no answer)'}
                            </Text>
                          </div>
                          {question.is_correct
                            ? <CheckCircle size={20} color="var(--sat-green)" style={{ flexShrink: 0 }} />
                            : <XCircle size={20} color="var(--sat-red)" style={{ flexShrink: 0 }} />}
                        </Group>
                      </Paper>
                      {!question.is_correct && question.correct_answer_text && (
                        <Paper p={14} radius="md" style={{ background: 'var(--sat-green-soft-subtle)', border: '2px solid var(--sat-green)' }}>
                          <Text size="xs" fw={700} c="var(--sat-text-faint)" mb={2}>Correct answer</Text>
                          <Text fw={700} size="lg" style={{ wordBreak: 'break-word' }}>{question.correct_answer_text}</Text>
                        </Paper>
                      )}
                    </Stack>
                  ) : (
                    <>
                      {question.question_type === 'multiple_choice' && (() => {
                        const selChoice = findChoiceForSubmission(
                          question.choices,
                          question.selected_choice_id,
                          question.selected_choice_label
                        )
                        const corChoice = findChoiceForSubmission(
                          question.choices,
                          question.correct_choice_id,
                          question.correct_choice_label
                        )
                        const studentLetter =
                          selChoice?.choice_label ?? question.selected_choice_label ?? null
                        const correctLetter =
                          corChoice?.choice_label ?? question.correct_choice_label ?? null
                        return (
                          <Paper p="sm" radius="md" style={{ background: 'var(--sat-surface-alt)', border: '1px solid var(--sat-border-soft)' }}>
                            <Text size="xs" fw={700} c="var(--sat-text-faint)" mb={6}>Student vs correct</Text>
                            <Text size="sm" c="var(--sat-text)">
                              {studentLetter ? (
                                <>
                                  <Text component="span" fw={700} c={question.is_correct ? 'var(--sat-green)' : 'var(--sat-red)'}>
                                    Chose ({studentLetter})
                                  </Text>
                                  {!question.is_correct && correctLetter && (
                                    <>
                                      <Text component="span" c="var(--sat-text-soft)"> — </Text>
                                      <Text component="span" fw={600} c="var(--sat-green)">Correct: ({correctLetter})</Text>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Text component="span" c="var(--sat-text-soft)">No answer selected</Text>
                                  {correctLetter && (
                                    <>
                                      <Text component="span" c="var(--sat-text-soft)"> — </Text>
                                      <Text component="span" fw={600} c="var(--sat-green)">Correct: ({correctLetter})</Text>
                                    </>
                                  )}
                                </>
                              )}
                            </Text>
                          </Paper>
                        )
                      })()}

                      <Stack gap={10}>
                        {question.choices.map((choice) => {
                          const isSelected =
                            sameChoiceId(question.selected_choice_id, choice.id) ||
                            sameChoiceLabel(question.selected_choice_label, choice.choice_label)
                          const isCorrect =
                            sameChoiceId(question.correct_choice_id, choice.id) ||
                            sameChoiceLabel(question.correct_choice_label, choice.choice_label)
                          const bg = isCorrect
                            ? 'var(--sat-green-soft-subtle)'
                            : isSelected
                              ? 'var(--sat-red-soft)'
                              : 'var(--sat-surface)'
                          const border = isCorrect
                            ? '2px solid var(--sat-green)'
                            : isSelected
                              ? '2px solid var(--sat-red)'
                              : '1px solid var(--sat-border-soft)'

                          return (
                            <Paper key={choice.id} p={14} radius="md" style={{ background: bg, border }}>
                              <Group gap={12} wrap="nowrap" align="flex-start">
                                <Group gap={6} wrap="nowrap" style={{ flexShrink: 0, alignItems: 'center' }}>
                                  <Text fw={700} size="sm" c="var(--sat-text-faint)" style={{ minWidth: 22 }}>
                                    {choice.choice_label}
                                  </Text>
                                  {isSelected && (
                                    <Badge size="xs" variant="light" color={isCorrect ? 'green' : 'red'}>
                                      Student
                                    </Badge>
                                  )}
                                  {isCorrect && !isSelected && (
                                    <Badge size="xs" variant="light" color="green">
                                      Correct
                                    </Badge>
                                  )}
                                </Group>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <RichText size="sm">{choice.choice_text}</RichText>
                                </Box>
                                {isCorrect && <CheckCircle size={18} color="var(--sat-green)" style={{ flexShrink: 0 }} />}
                                {!isCorrect && isSelected && <XCircle size={18} color="var(--sat-red)" style={{ flexShrink: 0 }} />}
                              </Group>
                            </Paper>
                          )
                        })}
                      </Stack>
                    </>
                  )}

                  {question.explanation && (
                    <Paper p={14} radius="md" style={{ background: 'var(--sat-bg)', border: '1px solid var(--sat-blush)' }}>
                      <Text size="xs" fw={700} c="var(--sat-text)" mb={4}>Explanation</Text>
                      <RichText size="sm">{question.explanation}</RichText>
                    </Paper>
                  )}
                </Stack>
              </Paper>
            ))}

            <Divider label="Finalize Grade" labelPosition="left" />

            <Paper shadow="sm" radius="md" p="xl">
              <Stack gap={16}>
                <NumberInput
                  label="Score (0–100)"
                  description="Leave blank to use the auto-calculated score."
                  placeholder={`Auto: ${Math.round(selectedDetail.auto_score)}%`}
                  min={0}
                  max={100}
                  value={score}
                  onChange={setScore}
                  suffix="%"
                />

                <Textarea
                  label="Feedback"
                  description="This will be visible to the student."
                  placeholder="Great work on algebra. Review rate problems for the next assignment..."
                  rows={4}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />

                <Group justify="flex-end" mt={8}>
                  <Button
                    variant="subtle"
                    color="gray"
                    onClick={() => { setSelectedStudent(null); setSelectedAssignment(null); setSelectedDetail(null) }}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="eveningSea"
                    leftSection={<CheckCircle size={16} />}
                    loading={grading}
                    onClick={handleGrade}
                  >
                    {selectedStudent.status === 'reviewed' ? 'Update Grade' : 'Submit Grade'}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Box>
    </Box>
  )
}
